import { useState } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { BarChart2, Menu, X, Sparkles, LogOut } from "lucide-react";
import {
  updateProfile,
  changePassword,
  setup2FA,
  enable2FA,
  disable2FA,
  resetOnboarding,
  powerbiApi,
} from "../api";
import { Toggle } from "../components/ui";
import { useAuth } from "../hooks/useAuth";
import { version } from "../../package.json";

const SECTIONS = [
  { key: "profile", label: "Profile" },
  { key: "password", label: "Password" },
  { key: "security", label: "Security" },
  { key: "connections", label: "Connections" },
  { key: "dashboard", label: "Dashboard setup" },
];

export default function SettingsPage() {
  const { logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);

  const tabParam = searchParams.get("tab");
  const active = SECTIONS.some((s) => s.key === tabParam) ? tabParam! : "profile";

  const activeLabel = SECTIONS.find((s) => s.key === active)?.label;

  function selectSection(key: string) {
    const next = new URLSearchParams(searchParams);
    if (key === "profile") next.delete("tab");
    else next.set("tab", key);
    setSearchParams(next, { replace: true });
    setMenuOpen(false);
  }

  return (
    <div className="flex h-full">
      {/* Desktop vertical nav */}
      <aside className="hidden md:flex md:flex-col w-52 shrink-0 border-r border-gray-200 bg-white py-6 px-3">
        <p className="px-3 mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Settings</p>
        <nav className="flex flex-col gap-0.5 flex-1">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => selectSection(s.key)}
              className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                active === s.key
                  ? "bg-[#1D345F]/10 text-[#1D345F]"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }`}
            >
              {s.label}
            </button>
          ))}
        </nav>
        <div className="mt-4 border-t border-gray-200 pt-4 flex flex-col gap-0.5">
          <NavLink
            to="/changelog"
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#1D345F]/10 text-[#1D345F]"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }`
            }
          >
            <Sparkles size={15} />
            <span>What's new</span>
            <span className="ml-auto text-xs text-gray-400">v{version}</span>
          </NavLink>
          <button
            onClick={logout}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        {/* Mobile header bar */}
        <div className="md:hidden flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100"
            aria-label="Toggle settings menu"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <span className="text-sm font-medium text-gray-800">{activeLabel}</span>
        </div>

        {/* Mobile dropdown nav */}
        {menuOpen && (
          <div className="md:hidden border-b border-gray-200 bg-white px-3 pb-3">
            <nav className="flex flex-col gap-0.5">
              {SECTIONS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => selectSection(s.key)}
                  className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    active === s.key
                      ? "bg-[#1D345F]/10 text-[#1D345F]"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  {s.label}
                </button>
              ))}
              <div className="mt-2 border-t border-gray-200 pt-2 flex flex-col gap-0.5">
                <NavLink
                  to="/changelog"
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-[#1D345F]/10 text-[#1D345F]"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`
                  }
                >
                  <Sparkles size={15} />
                  <span>What's new</span>
                  <span className="ml-auto text-xs text-gray-400">v{version}</span>
                </NavLink>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                >
                  <LogOut size={15} />
                  Sign out
                </button>
              </div>
            </nav>
          </div>
        )}

        <div className="p-6 md:p-8 max-w-lg">
          {active === "profile" && <ProfileSection />}
          {active === "password" && <PasswordSection />}
          {active === "security" && <SecuritySection />}
          {active === "connections" && <ConnectionsSection />}
          {active === "dashboard" && <DashboardSetupSection />}
        </div>
      </div>
    </div>
  );
}

/* ─── Profile ─────────────────────────────────────────────────── */

function ProfileSection() {
  const { user, refreshUser } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [agentName, setAgentName] = useState(user?.agent_name || "Brian");
  const [alertsEnabled, setAlertsEnabled] = useState(user?.status_alerts_enabled ?? true);
  const [slackUserId, setSlackUserId] = useState(user?.slack_user_id || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const isSuperAdmin = user?.role?.name === "super_admin";
  const hasSlackBot = Boolean(user?.permissions?.slack_bot);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload: {
        agent_name: string;
        full_name: string;
        status_alerts_enabled?: boolean;
        slack_user_id?: string | null;
      } = { agent_name: agentName, full_name: fullName };
      if (isSuperAdmin) payload.status_alerts_enabled = alertsEnabled;
      if (hasSlackBot) payload.slack_user_id = slackUserId.trim() || null;
      await updateProfile(payload);
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
            {user?.role?.name ?? "—"}
          </span>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Your name</label>
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">AI agent name</label>
        <input
          value={agentName}
          onChange={(e) => setAgentName(e.target.value)}
          placeholder="e.g. Brian, Max, Aria..."
          maxLength={50}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">The name shown in chat for your AI assistant.</p>
      </div>

      {hasSlackBot && (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Your Slack member ID</label>
          <input
            value={slackUserId}
            onChange={(e) => setSlackUserId(e.target.value)}
            placeholder="e.g. U0123456789"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-400">
            Link your Slack account so Brian can DM you (chat replies and scheduled report deliveries). Find your member ID in Slack under your profile &rarr; More &rarr; Copy member ID.
          </p>
        </div>
      )}

      {isSuperAdmin && (
        <div className="rounded-xl border border-gray-200 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Notifications</p>
          <div className="flex items-start gap-3">
            <div className="mt-0.5 shrink-0">
              <Toggle checked={alertsEnabled} onChange={setAlertsEnabled} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Service status email alerts</p>
              <p className="text-xs text-gray-400">
                Receive an email when a monitored service goes offline or recovers.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end">
        <SaveButton loading={loading} saved={saved} label="Save profile" />
      </div>
    </form>
  );
}

/* ─── Password ────────────────────────────────────────────────── */

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("Passwords do not match"); return; }
    if (next.length < 8) { setError("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      await changePassword(current, next);
      setSaved(true);
      setCurrent(""); setNext(""); setConfirm("");
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Current password</label>
        <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">New password</label>
        <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Confirm new password</label>
        <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end">
        <SaveButton loading={loading} saved={saved} label="Change password" savedLabel="Changed!" />
      </div>
    </form>
  );
}

/* ─── Security (2FA) ──────────────────────────────────────────── */

function SecuritySection() {
  const { user, refreshUser } = useAuth();
  const [setupData, setSetupData] = useState<{ otpauth_uri: string; secret: string } | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [showDisableForm, setShowDisableForm] = useState(false);

  async function startSetup() {
    setError("");
    setLoading(true);
    try {
      const res = await setup2FA();
      setSetupData(res.data);
      setCode("");
    } catch {
      setError("Failed to start 2FA setup");
    } finally {
      setLoading(false);
    }
  }

  async function confirmEnable(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await enable2FA(code);
      await refreshUser();
      setSetupData(null);
      setCode("");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || "Invalid code");
      setCode("");
    } finally {
      setLoading(false);
    }
  }

  async function confirmDisable(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await disable2FA(disablePassword);
      await refreshUser();
      setShowDisableForm(false);
      setDisablePassword("");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || "Incorrect password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h2 className="mb-1 text-sm font-semibold text-gray-800">Two-factor authentication</h2>
      <p className="mb-5 text-sm text-gray-500">Add an extra layer of security to your account with an authenticator app.</p>

      {user?.totp_enabled ? (
        showDisableForm ? (
          <form onSubmit={confirmDisable} className="space-y-4">
            <p className="text-sm text-gray-700">Enter your password to disable 2FA.</p>
            <input type="password" required placeholder="Your password" value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => { setShowDisableForm(false); setError(""); setDisablePassword(""); }}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={loading}
                className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {loading ? "Disabling..." : "Disable 2FA"}
              </button>
            </div>
          </form>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <span className="text-sm font-medium text-green-700">2FA enabled</span>
            <button onClick={() => { setShowDisableForm(true); setError(""); }}
              className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">
              Disable
            </button>
          </div>
        )
      ) : setupData ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.</p>
          <div className="flex justify-center py-2">
            <QRCodeSVG value={setupData.otpauth_uri} size={160} />
          </div>
          <p className="text-xs text-gray-500 text-center break-all">
            Can't scan? Use this secret: <span className="font-mono font-medium">{setupData.secret}</span>
          </p>
          <form onSubmit={confirmEnable} className="space-y-3">
            <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required
              placeholder="6-digit code" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-center tracking-widest focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => { setSetupData(null); setError(""); setCode(""); }}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={loading || code.length !== 6}
                className="flex-1 rounded-lg bg-[#1D345F] px-3 py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50">
                {loading ? "Verifying..." : "Enable 2FA"}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <span className="text-sm text-gray-600">Not enabled</span>
          <button onClick={startSetup} disabled={loading}
            className="rounded-lg bg-[#1D345F] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50">
            {loading ? "Loading..." : "Set up 2FA"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Connections ─────────────────────────────────────────────── */

function ConnectionsSection() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function connectPbi() {
    setError("");
    setLoading(true);
    try {
      const url = await powerbiApi.getAuthorizeUrl();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError("Failed to get authorization URL. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function disconnectPbi() {
    setError("");
    setLoading(true);
    try {
      await powerbiApi.disconnect();
      await refreshUser();
    } catch { return; } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h2 className="mb-1 text-sm font-semibold text-gray-800">Connections</h2>
      <p className="mb-5 text-sm text-gray-500">Connect third-party services to extend what your AI assistant can do.</p>

      <div className="rounded-lg border border-gray-200 divide-y divide-gray-200">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-yellow-50 text-yellow-600">
              <BarChart2 size={18} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">Power BI</p>
              <p className="text-xs text-gray-400">
                {user?.pbi_connected ? "Connected" : "Create datasets and view reports in chat"}
              </p>
            </div>
          </div>
          {user?.pbi_connected ? (
            <button onClick={disconnectPbi} disabled={loading}
              className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50">
              {loading ? "Disconnecting..." : "Disconnect"}
            </button>
          ) : (
            <button onClick={connectPbi} disabled={loading}
              className="rounded-lg bg-[#1D345F] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50">
              {loading ? "Opening..." : "Connect"}
            </button>
          )}
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/* ─── Dashboard setup ─────────────────────────────────────────── */

function DashboardSetupSection() {
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function handleRerun() {
    setError("");
    setLoading(true);
    try {
      await resetOnboarding();
      await refreshUser();
      setDone(true);
    } catch {
      setError("Failed to reset. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h2 className="mb-1 text-sm font-semibold text-gray-800">Dashboard setup</h2>
      <p className="mb-5 text-sm text-gray-500">
        Rerun the onboarding wizard to generate a new starter dashboard based on what you care about. Your existing dashboard tiles will not be removed automatically.
      </p>

      {done ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          Done — the setup wizard will appear on your next page load.
        </div>
      ) : (
        <button onClick={handleRerun} disabled={loading}
          className="rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50">
          {loading ? "Resetting..." : "Rerun setup wizard"}
        </button>
      )}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/* ─── Shared helpers ──────────────────────────────────────────── */

function SaveButton({ loading, saved, label, savedLabel = "Saved!" }: { loading: boolean; saved: boolean; label: string; savedLabel?: string }) {
  return (
    <button type="submit" disabled={loading}
      className="rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50">
      {saved ? savedLabel : loading ? "Saving..." : label}
    </button>
  );
}
